import { isEmbeddedApp, getShopifySessionToken } from './shopifyAppBridge';

/** Resolve at request time — never at module load (build has no window). */
function resolveApiBase(): string {
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        // Same-origin /api on the app domain avoids CORS in the Shopify iframe
        if (host === 'app.trycommerium.com' || host.endsWith('.vercel.app')) {
            return '';
        }
    }

    const envUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '');
    if (envUrl) {
        return envUrl;
    }

    return 'https://api.trycommerium.com';
}

export function getApiBase(): string {
    return resolveApiBase();
}

export function getShopDomain(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get('shop');
    if (shop) {
        localStorage.setItem('shopify_shop_domain', shop);
        return shop;
    }
    return localStorage.getItem('shopify_shop_domain');
}

export async function apiFetch(endpoint: string, options: RequestInit = {}) {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
    };

    if (isEmbeddedApp()) {
        try {
            const sessionToken = await getShopifySessionToken();
            headers['Authorization'] = `Bearer ${sessionToken}`;
            headers['X-Shopify-Session-Token'] = sessionToken;
        } catch (error) {
            console.warn('Failed to get session token, falling back to stored token:', error);
            const token = localStorage.getItem('artisan_token');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }
    } else {
        const token = localStorage.getItem('artisan_token');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    const shopDomain = getShopDomain();
    if (shopDomain) {
        headers['X-Shop-Domain'] = shopDomain;
    }

    const response = await fetch(`${resolveApiBase()}${endpoint}`, {
        ...options,
        headers,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(error.message || error.error || 'Request failed');
    }

    return response.json();
}

export function getApiUrl(endpoint: string): string {
    return `${resolveApiBase()}${endpoint}`;
}

export function getApiHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    const token = localStorage.getItem('artisan_token');
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const shopDomain = getShopDomain();
    if (shopDomain) {
        headers['X-Shop-Domain'] = shopDomain;
    }

    return headers;
}

export async function getApiHeadersAsync(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    if (isEmbeddedApp()) {
        try {
            const sessionToken = await getShopifySessionToken();
            headers['Authorization'] = `Bearer ${sessionToken}`;
            headers['X-Shopify-Session-Token'] = sessionToken;
        } catch (error) {
            console.warn('Failed to get session token:', error);
            const token = localStorage.getItem('artisan_token');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }
    } else {
        const token = localStorage.getItem('artisan_token');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    const shopDomain = getShopDomain();
    if (shopDomain) {
        headers['X-Shop-Domain'] = shopDomain;
    }

    return headers;
}

export { isEmbeddedApp, getShopifySessionToken } from './shopifyAppBridge';
export default { apiFetch, getApiUrl, getApiBase, getShopDomain, getApiHeaders, getApiHeadersAsync, isEmbeddedApp };
